import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { Alert, Col, Modal, Input } from 'components/bootstrap';
import { Spinner } from 'components/common';

import FileSystemRepositoryModalForm from './FileSystemRepositoryModalForm';
import CloudStorageRepositoryModalForm from './CloudStorageRepositoryModalForm';
import { STORAGE_REPOSITORY_TYPE } from './types';
import type { StorageRepositoryType } from './types';

import useDataTiering from '../hooks/useDataTiering';

type Props = {
  show: boolean;
  hideModal: () => void;
};

const RepositoryLocationSelect = styled(Col)(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    font-size: ${theme.fonts.size.large};
    gap: ${theme.spacings.md};
    margin-top: ${theme.spacings.md};
    margin-bottom: ${theme.spacings.md};
    padding-left: ${theme.spacings.lg};
    padding-right: ${theme.spacings.lg};
  `,
);

const LocationHeading = styled.p`
  font-weight: bold;
  margin-bottom: 0;
`;

const CreateRepositoryModal = ({ hideModal, show }: Props) => {
  const { readiness } = useDataTiering();
  const [selectedRepoLocation, setSelectedRepoLocation] = useState<StorageRepositoryType | undefined>(undefined);

  const repositoryLocationLabels = {
    s3: 'Amazon S3',
    gcs: 'Google Cloud Storage',
    fs: 'File System',
  };

  useEffect(() => {
    if (!readiness.isLoading && readiness.data?.details.supported_repository_types) {
      setSelectedRepoLocation(readiness.data?.details.supported_repository_types[0]);
    }
  }, [readiness.isLoading, readiness.data?.details.supported_repository_types]);

  const repositoryForm = () => {
    if (readiness.isLoading) return <Spinner text="Fetching configuration information..." />;

    if (readiness.data?.details.supported_repository_types.length === 0) {
      return (
        <Alert bsStyle="info">
          No supported warm storage repository types. Please configure a warm storage repository location.
        </Alert>
      );
    }

    return (
      <>
        {readiness.data?.details.supported_repository_types.length > 1 && (
          <RepositoryLocationSelect>
            <LocationHeading>Warm Storage Repository Location:</LocationHeading>
            {readiness.data?.details.supported_repository_types.map((type) => (
              <Input
                checked={selectedRepoLocation === type}
                formGroupClassName=""
                id="repository_type"
                key={type}
                label={repositoryLocationLabels[type]}
                onChange={() => setSelectedRepoLocation(type)}
                type="radio"
                value={type}
              />
            ))}
          </RepositoryLocationSelect>
        )}

        {(selectedRepoLocation === STORAGE_REPOSITORY_TYPE.S3 ||
          selectedRepoLocation === STORAGE_REPOSITORY_TYPE.GCS) && (
          <CloudStorageRepositoryModalForm hideModal={hideModal} type={selectedRepoLocation} />
        )}

        {selectedRepoLocation === 'fs' && <FileSystemRepositoryModalForm hideModal={hideModal} />}
      </>
    );
  };

  return (
    <Modal show={show} bsSize="large" onHide={hideModal}>
      <Modal.Header>
        <Modal.Title>
          Create {selectedRepoLocation && `${repositoryLocationLabels[selectedRepoLocation]} `}Warm Storage Repository
        </Modal.Title>
      </Modal.Header>
      {repositoryForm()}
    </Modal>
  );
};

export default CreateRepositoryModal;
